// ============================================================
// Client Settings — persisted in localStorage, adjustable from
// the pause menu at runtime.
// ============================================================

export type ShadowQuality = 'off' | 'low' | 'high';
export type ResolutionScale = 'low' | 'medium' | 'high';
export type ParticleDensity = 'off' | 'low' | 'high';

export interface ClientSettingsData {
  shadowQuality: ShadowQuality;
  resolutionScale: ResolutionScale;
  particleDensity: ParticleDensity;
}

const STORAGE_KEY = 'cart-racer-settings';

const DEFAULTS: ClientSettingsData = {
  shadowQuality: 'high',
  resolutionScale: 'high',
  particleDensity: 'high',
};

type Listener = () => void;

class ClientSettingsManager {
  private data: ClientSettingsData;
  private listeners: Listener[] = [];

  constructor() {
    this.data = { ...DEFAULTS };
    this.load();
  }

  get(): ClientSettingsData {
    return { ...this.data };
  }

  set(partial: Partial<ClientSettingsData>): void {
    Object.assign(this.data, partial);
    this.save();
    this.notify();
  }

  onChange(fn: Listener): void {
    this.listeners.push(fn);
  }

  // ── Resolved values ──

  getShadowMapSize(isMobile: boolean): number {
    switch (this.data.shadowQuality) {
      case 'off': return 0;
      case 'low': return isMobile ? 256 : 512;
      case 'high': return isMobile ? 512 : 1024;
    }
  }

  isShadowEnabled(): boolean {
    return this.data.shadowQuality !== 'off';
  }

  getPixelRatio(isMobile: boolean): number {
    switch (this.data.resolutionScale) {
      case 'low': return isMobile ? 0.5 : 0.75;
      case 'medium': return isMobile ? 0.75 : 1.0;
      case 'high': return isMobile ? 1.0 : Math.min(window.devicePixelRatio, 1.5);
    }
  }

  getParticleScale(): number {
    switch (this.data.particleDensity) {
      case 'off': return 0;
      case 'low': return 0.3;
      case 'high': return 1.0;
    }
  }

  // ── Persistence ──

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          if (parsed.shadowQuality) this.data.shadowQuality = parsed.shadowQuality;
          if (parsed.resolutionScale) this.data.resolutionScale = parsed.resolutionScale;
          if (parsed.particleDensity) this.data.particleDensity = parsed.particleDensity;
        }
      }
    } catch {
      // ignore corrupt localStorage
    }
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      // localStorage might be full or disabled
    }
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }
}

/** Singleton client settings instance */
export const clientSettings = new ClientSettingsManager();
